import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibChevronDownIcon } from '@chewy/kib-icons-react'
import { KibSpinner } from '@chewy/kib-interstitials-react'
import type { Nil, User } from '@pbt/pbt-ui-components'

import RhapActionsPopper from '~/components/common/RhapActionsPopper'
import DialogNames from '~/constants/DialogNames'
import { updateConversationRecipientContext } from '~/store/actions/conversations'
import {
  getPatientsIsLoading,
  getPatientsList,
} from '~/store/reducers/patients'
import useDialog from '~/utils/useDialog'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'

const AddPatientButton = ({
  client,
  conversationId,
}: {
  client: User | Nil
  conversationId: string
}) => {
  const { t } = useTranslation(['Common'])
  const dispatch = useDispatch()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [openAddClientPatientToConversationDialog] = useDialog(
    DialogNames.ADD_CLIENT_PATIENT_TO_CONVERSATION,
  )
  const isLoadingPatients = useSelector(getPatientsIsLoading)
  const patients = useSelector(getPatientsList(client?.patients))

  if (!client) {
    return (
      <KibButtonNew
        emphasis="tertiary"
        theme="action"
        onClick={() =>
          openAddClientPatientToConversationDialog({ conversationId })
        }
      >
        {t('Common:ADD_CLIENT_AND_PATIENT')}
      </KibButtonNew>
    )
  }

  const addPatient = (patientId: string) => {
    dispatch(
      updateConversationRecipientContext(conversationId, {
        clientId: client.id,
        patientId,
      }),
    )
  }

  const actions = patients.map((patient) => ({
    id: patient.id,
    label: patient.name,
    onClick: () => addPatient(patient.id),
  }))

  return (
    <>
      <KibButtonNew
        emphasis="tertiary"
        icon={isLoadingPatients ? <KibSpinner /> : <KibChevronDownIcon />}
        iconPosition="end"
        loading={isLoadingPatients}
        ref={buttonRef}
        theme="action"
      >
        {t('Common:ADD_PATIENT')}
      </KibButtonNew>
      <RhapActionsPopper
        usePortal
        actions={actions}
        placement="bottom"
        trigger={buttonRef}
      />
    </>
  )
}

export default AddPatientButton
